import {
  defaultServicePagination,
  type ServiceCursorPaginatedResponseType,
} from '@/connect-types/backend/service'

/*
Stephen Davies
15:31
Hey, wondering if you can take a look at this and see if it’s something you can work with for front end?
(no auth on the routes yet, but that will be added)
# paginated list of all threads, ordered by updated_at DESC
GET https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads

# view contacts on the thread, these will be people who will receive a notification when a user replies
GET https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads/ef4c52ec-9bc4-4c31-806b-9c90c111d4ca/contacts

# add a new contact to a thread, need to provide method, name and email
POST https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads/ef4c52ec-9bc4-4c31-806b-9c90c111d4ca/contacts

# remove a contact from the thread
DELETE https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads/ef4c52ec-9bc4-4c31-806b-9c90c111d4ca/contacts/{id}

# view paginated list of all events on the thread, this will include things like emails, notes, messages
GET https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads/ef4c52ec-9bc4-4c31-806b-9c90c111d4ca/events

# post a private note to a thread
POST https://inbox.stg.stampede.ai/f163ff62-2116-11ec-bd66-0a58a9feac02/threads/ef4c52ec-9bc4-4c31-806b-9c90c111d4ca/notes
*/

export interface EventableOutgoingEmailType {
  id: string
  to_email: string
  to_name: string
  subject: string

  created_at: Date

  sent_at: Date | null
  delivered_at: Date | null
  first_opened_at: Date | null
}

export interface EventableContactType {
  email: string
  id: string
  name: string
  type: 'from' | 'to' | 'cc'
}

export type EventableType =
  | {
      eventable_type: 'Email'
      eventable: InboxThreadEventEventableEmailType
    }
  | {
      eventable_type: 'Message'
      eventable: InboxThreadEventEventableMessageType
    }
  | {
      eventable_type: 'Note'
      eventable: InboxThreadEventEventableNoteType
    }
  | {
      eventable_type: 'System Note'
      eventable: InboxThreadEventEventableSystemNoteType
    }
  | {
      eventable_type: 'Incoming SMS Message'
      eventable: InboxThreadEventEventableSmsMessage
    }
  | {
      eventable_type: 'Incoming Form Submission'
      eventable: InboxThreadEventEventableIncomingFormSubmissionType
    }
  | {
      eventable_type: 'Contact Reply'
      eventable: InboxThreadEventEventableContactReplyType
    }

export type InboxThreadEventType = {
  id: string
  created_at: Date
} & EventableType

export type InboxContextTypes =
  | 'booking'
  | 'review'
  | 'gifting'
  | 'profile'
  | 'form'

export interface InboxThreadType {
  id: string
  created_at: Date
  updated_at: Date
  archived_at: Date
  subject: string
  contacts: InboxThreadContactsType[]
  last_thread_event_at: Date | null
  assignees: InboxThreadAssigneeType[]
  accepted_message_text_formats: ['html', 'text']
  last_public_outgoing_event_at: string | null | undefined
  read_at: Date | null
  tags: InboxTagType[]
  serial?: string
  context_type?: InboxContextTypes
  context_id?: string
  /**
   * FOR FRONT END USE
   */
  isLoading?: boolean
  events: ServiceCursorPaginatedResponseType<InboxThreadEventType>
  no_event_data: boolean
  method: 'sms' | 'email'
}

export interface InboxThreadLastEventV2 {
  id: string
  last_public_outgoing_event_at: string | null
  last_public_incoming_event_at: string | null
  last_thread_event_at: string | null
  read_at: string | null
  archived_at: string | null
}

export interface InboxThreadsLastEventsV2Response {
  threads: InboxThreadLastEventV2[]
}

export interface InboxThreadAssigneeType {
  id: string
  thread_id: string
  oauth_user_id: string
  label: 'owner' | 'subscriber'
  assigned_to_data: {
    uid: string
    email: string
    first: string
    last: string
  }
  created_at: Date
  updated_at: Date
}

export interface InboxThreadContactsType {
  id: string
  method: 'email'
  name: string
  email: string
  org_reg_id: string
  mobile: string
}

export interface InboxThreadEventContactType {
  id: string
  org_reg_id: string
  method: 'email' | 'sms' | null
  name: string
  email: string | null
  mobile: string | null
}

export interface InboxThreadEventEventableContactReplyType {
  id: string
  thread_contact: InboxThreadEventContactType
  description: string
  created_at: Date
}

export interface InboxThreadEventEventableNoteType {
  created_at: Date
  description: string
  id: string
  user_email: string
  user_id: string
  user_name: string
}

export interface InboxThreadEventEventableSystemNoteType {
  created_at: Date
  description: string
  format: 'text'
  id: string
}

export interface InboxThreadEventEventableSmsMessage {
  created_at: Date
  id: string
  from: string
  message: string
  recieved_at: string // @todo spelling
  to: string
}

export interface InboxThreadEventEventableIncomingFormSubmissionType {
  id: string
  entries: {
    question: string
    answers: string
  }[]
  created_at: Date
  updated_at: Date
}

export interface InboxThreadEventEventableMessageType {
  created_at: Date
  description: string
  id: string
  user_email: string
  user_id: string
  user_name: string
  outgoing_emails: EventableOutgoingEmailType[]
}

export interface InboxThreadEventEventableEmailType {
  created_at: Date
  html_body: string
  html_body_trimmed: string
  id: string
  sent_at: string
  subject: string
  text_body: string
  contacts: EventableContactType[]
  attachments: {
    content_type: string
    filename: string
    url: string
  }[]
}

export const initThread: InboxThreadType = {
  events: defaultServicePagination,
  id: null,
  created_at: new Date(),
  subject: '',
  updated_at: new Date(),
  contacts: [],
  archived_at: new Date(),
  read_at: null,
  last_thread_event_at: new Date(),
  no_event_data: false,
  method: 'email',
}

export interface InboxTagType {
  id: string
  name: string
  //@todo remove - think wont be using this
  value?: null
  created_at: string
}

export interface InboxSettingsDataType {
  filter_auto_replies: boolean
}

export interface InboxMessageTemplateType {
  id: string
  name: string
  serial: string | null
  format: 'html' | 'text'
  body: string
  created_at: string
  updated_at: string
}

export interface InboxMessageTemplatePayload {
  name: string
  body: string
  format: 'html' | 'text'
  serial?: string | null
}

export interface InboxRenderedTemplate {
  body: string
  format: 'html' | 'text'
}

export interface InboxAssetType {
  id: string
  original_filename: string
  mime_type: string
  size_bytes: number | null
  folder: string | null
  serial: string | null
  thread_id: string | null
  url: string
  created_at: string
}

export interface InboxAssetInitiatePayload {
  filename: string
  mime_type: string
  size: number
  folder?: string | null
  serial?: string | null
  thread_id?: string | null
}

export interface InboxAssetInitiateResponse {
  asset_id: string
  upload_url: string
  headers: Record<string, string>
}

export interface VirtualNumberType {
  id: string
  organisation_id: string
  serial: string | null | undefined
  number: string
  description: string | null | undefined
  provider: string
  created_at: string
  updated_at: string
}
